import React from 'react';
import { Container, Typography, Paper, Box, Accordion, AccordionSummary, AccordionDetails, List, ListItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { ExpandMore, Calculate, TrendingUp, Assessment, Warning, BarChart, Description, Lock, ErrorOutline, CheckCircle } from '@mui/icons-material';

const MethodologyPage = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Аналитичен подход и предизвикателства
        </Typography>

         <Divider sx={{ my: 3 }} />
        
        <Typography paragraph sx={{ mb: 3 }}>
          Този раздел описва аналитичния подход при обработката на данните в Общинския барометър, както и идентифицираните предизвикателства пред статистическата отчетност в сектора. Повече подробности за методологията може да откриете в Доклада „Читалищната мрежа в България – анализ през призмата на данните“.
        </Typography>
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            Приоритетност на данните
          </Typography>
          
          <Typography paragraph sx={{ mb: 2 }}>
            В рамките на изследването е приложена йерархия на достоверност. С предимство са ползвани данните от Националния статистически институт (НСИ), Министерството на финансите (МФ) и Националната агенция по приходите (НАП), както и икономическите показатели на ИПИ.
          </Typography>
          
          <Paper variant="outlined" sx={{ p: 3, backgroundColor: 'rgba(25, 118, 210, 0.05)' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning fontSize="small" />
              Внимание към ограниченията
            </Typography>
            <Typography variant="body2">
              Данните от регистрите към Министерството на културата и Търговския регистър са използвани селективно и с повишено внимание. Причините за това са установени системни несъответствия, липса на актуална отчетност от страна на част от читалищата и технически ограничения при извличането на пълните финансови масиви.
            </Typography>
          </Paper>
        </Box>
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'warning.main' }}>
            Парадокс на „претоварената отчетност"
          </Typography>
          
          <Typography paragraph sx={{ mb: 2 }}>
            Изследването осветява парадоксална ситуация: читалищата подават информация за дейността си на поне шест различни места, но въпреки това липсва изчерпателна и надеждна картина за сектора. Липсата на координация между институциите (МК, МФ, НСИ, НАП) води до:
          </Typography>
          
          <List>
            <ListItem sx={{ alignItems: 'flex-start' }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                <ErrorOutline color="warning" fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Административно претоварване" 
                secondary="На читалищата с дублиращи се отчети." 
              />
            </ListItem>
            
            <ListItem sx={{ alignItems: 'flex-start' }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                <ErrorOutline color="warning" fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Липса на верификация" 
                secondary="Което позволява сериозни разминавания в базисни показатели (брой активни институции, приходи и персонал)." 
              />
            </ListItem>
          </List>
        </Box>
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'error.main' }}>
            Ограничения и информационни дефицити
          </Typography>
          
          <Typography paragraph sx={{ mb: 2 }}>
            При ползването на продуктите трябва да се имат предвид следните обективни ограничения:
          </Typography>
          
          <Box sx={{ pl: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Lock fontSize="small" />
                Конфиденциалност
              </Typography>
              <Typography variant="body2">
                По силата на Закона за статистиката, НСИ и НАП не предоставят данни за общини с три или по-малко читалища (общо 20 общини, сред които Трън, Копривщица, Челопеч и др.), за да се избегне разкриването на индивидуална информация.
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <BarChart fontSize="small" />
                Липса на детайлност
              </Typography>
              <Typography variant="body2">
                Информацията от официалните източници е обобщена на ниво община, което не позволява автоматизиран анализ на спецификите „град-село".
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Description fontSize="small" />
                Дейности и библиотеки
              </Typography>
              <Typography variant="body2">
                Данните за културни състави и библиотечна дейност се базират на самоотчети без външна проверка и често се дублират в различните регистри.
              </Typography>
            </Box>
          </Box>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Специфични бележки
          </Typography>

           <Divider sx={{ my: 3 }} />
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              Обхват на читалищата
            </Typography>
            <Typography paragraph>
              В Базата данни и Барометъра са включени 3597 читалища с вписан БУЛСТАТ/ЕИК номер. Изключени са над 170 записи, представляващи „празни" профили, тестови записи или закрити институции, които все още фигурират в официалния регистър.
            </Typography>
          </Box>
          
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'error.main' }}>
              Регистър на читалищата и читалищните сдружения към МК
            </Typography>
            <Typography paragraph sx={{ mb: 1 }}>
              Идентифицирани са критични проблеми в поддръжката на Регистъра:
            </Typography>
            
            <List dense>
              <ListItem sx={{ alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: 30, mt: 0.3 }}>
                  <ErrorOutline color="error" fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Техническа нефункционалност" 
                  secondary="Блокирана отчетност за 2024/2025 г." 
                />
              </ListItem>
              
              <ListItem sx={{ alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: 30, mt: 0.3 }}>
                  <ErrorOutline color="error" fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Неактуални записи" 
                  secondary="Наличие на закрити читалища със статус „действащи"
                />
              </ListItem>
              
              <ListItem sx={{ alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: 30, mt: 0.3 }}>
                  <ErrorOutline color="error" fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Липса на информация" 
                  secondary="За около 25% от субсидираните бройки в страната спрямо данните на Министерството на финансите." 
                />
              </ListItem>
            </List>
          </Box>
        </Box>
      </Paper>
      
      <Paper elevation={2} sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Методология на изчисляване
        </Typography>
        
        <Typography paragraph sx={{ mb: 3 }}>
          Всички показатели в платформата се изчисляват по строго дефинирани методи,
          базирани на официални статистически източници.
        </Typography>
        
        <Box sx={{ mt: 2 }}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box display="flex" alignItems="center" gap={2}>
                <Calculate color="primary" />
                <Typography sx={{ fontWeight: 500 }}>Държавна субсидия на човек</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography sx={{ mb: 1 }}>
                <strong>Формула:</strong> (Брой субсидирани бройки × 19,555 лв.) / Население на общината
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle fontSize="small" />
                Източник: Данни от Министерство на културата за 2025 г.
              </Typography>
            </AccordionDetails>
          </Accordion>
          
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box display="flex" alignItems="center" gap={2}>
                <TrendingUp color="primary" />
                <Typography sx={{ fontWeight: 500 }}>Процент на приходите от субсидии</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography sx={{ mb: 1 }}>
                <strong>Формула:</strong> (Приходи от субсидии / Общи приходи) × 100
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle fontSize="small" />
                Източник: НСИ, 2022 г.
              </Typography>
            </AccordionDetails>
          </Accordion>
          
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box display="flex" alignItems="center" gap={2}>
                <Assessment color="primary" />
                <Typography sx={{ fontWeight: 500 }}>Читалища на 10,000 жители</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography sx={{ mb: 1 }}>
                <strong>Формула:</strong> (Брой читалища / Население) × 10,000
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle fontSize="small" />
                Източник: НСИ, 2023 г.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Paper>
    </Container>
  );
};

export default MethodologyPage;